import { Slider as FplSlider, Text } from '@figma/fpl-components';
import { useId } from 'react';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { Box } from '@/UI/Box/Box';
import { FormControlLabel } from '@/UI/FormControlLabel/FormControlLabel';
import { LineInput } from '@/UI/Input/Input';
import { Slider } from '@/UI/Slider/Slider';
import { FlexCenVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { maxWeight, minWeight } from '../Views/LoraView';

const stepWeight = 0.01;

const styles = stylex.create({
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer2,
    marginTop: spacer.spacer2,
    minWidth: 0,
    width: '100%',
  },
  label: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'inline-flex',
    flexGrow: 1,
    gap: spacer.spacer2,
    minWidth: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  labelDisabled: {
    cursor: 'default',
  },
  labelText: {
    flexShrink: 0,
  },
  valueInput: {
    flexShrink: 0,
    fontSize: '0.75rem',
  },
});

type MultiLoraWeight = number | number[] | string;

interface MultiLoraWeightFieldProps {
  disabled: boolean;
  label: string;
  onChange: (weight: MultiLoraWeight) => void;
  value: number;
}

function LegacyMultiLoraWeightField({ disabled, label, onChange, value }: MultiLoraWeightFieldProps) {
  return (
    <FlexCenVer sx={{ mt: 1, gap: 1 }}>
      <FormControlLabel
        sx={{ flexGrow: 1, ml: 0 }}
        label={label}
        labelPlacement="start"
        disabled={disabled}
        control={
          <Slider
            size="small"
            value={value}
            onChange={(_event, newValue) => {
              onChange(newValue);
            }}
            aria-labelledby="weight-slider"
            max={maxWeight}
            min={minWeight}
            sx={{ mx: 2 }}
            step={stepWeight}
            valueLabelDisplay="auto"
            disabled={disabled}
          />
        }
      />
      <Box sx={{ fontSize: '0.75rem' }}>
        <LineInput
          value={value}
          size="small"
          onChange={(event) => onChange(event.target.value)}
          inputProps={{
            step: stepWeight,
            min: minWeight,
            max: maxWeight,
            type: 'number',
            'aria-label': label,
          }}
          sx={{
            width: '50px',
            fontSize: '10px',
          }}
          disabled={disabled}
        />
      </Box>
    </FlexCenVer>
  );
}

function FplWeightSlider({
  disabled,
  labelId,
  onChange,
  value,
}: {
  disabled: boolean;
  labelId: string;
  onChange: (weight: MultiLoraWeight) => void;
  value: number;
}) {
  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <FplSlider
        size="sm"
        value={value}
        min={minWeight}
        rangeAnchor={minWeight}
        max={maxWeight}
        step={stepWeight}
        bigStep={stepWeight * 10}
        onChange={(next) => onChange(next)}
        aria-labelledby={labelId}
        disabled={disabled}
      />
    </Box>
  );
}

function FplWeightInput({
  disabled,
  label,
  onChange,
  value,
}: {
  disabled: boolean;
  label: string;
  onChange: (weight: MultiLoraWeight) => void;
  value: number;
}) {
  return (
    <LineInput
      aria-label={label}
      value={value}
      size="small"
      onChange={(event) => onChange(event.target.value)}
      inputProps={{
        step: stepWeight,
        min: minWeight,
        max: maxWeight,
        type: 'number',
        'aria-label': label,
      }}
      sx={{
        width: '50px',
        fontSize: '10px',
      }}
      disabled={disabled}
    />
  );
}

function FplMultiLoraWeightField({ disabled, label, onChange, value }: MultiLoraWeightFieldProps) {
  const labelId = useId();

  return (
    <div {...stylex.props(styles.row)}>
      <label {...stylex.props(styles.label, disabled && styles.labelDisabled)} aria-disabled={disabled || undefined}>
        <Text.span
          {...stylex.props(styles.labelText)}
          id={labelId}
          variant="body-medium"
          color={disabled ? 'disabled' : 'default'}
        >
          {label}
        </Text.span>
        <FplWeightSlider disabled={disabled} labelId={labelId} onChange={onChange} value={value} />
      </label>
      <div {...stylex.props(styles.valueInput)}>
        <FplWeightInput disabled={disabled} label={label} onChange={onChange} value={value} />
      </div>
    </div>
  );
}

export function MultiLoraWeightField(props: MultiLoraWeightFieldProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  return isFplMigrationEnabled ? <FplMultiLoraWeightField {...props} /> : <LegacyMultiLoraWeightField {...props} />;
}
